import React, { useCallback, useEffect } from 'react';
import type { FC } from 'react';

import { ModalTransition } from '@atlaskit/modal-dialog';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import {
	confluenceLocalStorageInstance as localStorage,
	keys as localStorageKeys,
} from '@confluence/storage-manager';
import { fg } from '@confluence/feature-gating';

import { useNewFeatureAdminOnboardingCoordination } from '../common/useNewFeatureAdminOnboardingCoordination';

import { LoomBetaAdminOptInOnboardingModal } from './LoomBetaAdminOptInOnboardingModal';

const LOOM_EP_MESSAGE_ID = 'cc-loom-beta-announcement-modal';

export const LoomBetaAdminOptInOnboardingModalManagerComponent: FC = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLoomTempEnabledViaLocalStorage = localStorage.getItemAsBoolean(
		localStorageKeys.LOOM_BETA_ENABLED,
	);

	const isOptInEnabled =
		!(fg('confluence_loom_beta_enabled') || isLoomTempEnabledViaLocalStorage) &&
		fg('confluence_loom_beta_opt_in');

	useEffect(() => {
		if (isOptInEnabled) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'loomBetaEligibleOptInEmailTrigger',
					source: 'globalNav',
				},
			}).fire();
		}
	}, [isOptInEnabled, createAnalyticsEvent]);

	const [showOnboarding, stopOnboarding] = useNewFeatureAdminOnboardingCoordination({
		isOnboardingEnabled: isOptInEnabled && fg('confluence_loom_beta_admin_onboarding'),
		messageId: LOOM_EP_MESSAGE_ID,
	});

	const handleClose = useCallback(() => {
		stopOnboarding();
	}, [stopOnboarding]);

	return (
		<ModalTransition>
			{showOnboarding && <LoomBetaAdminOptInOnboardingModal onClose={handleClose} />}
		</ModalTransition>
	);
};

export const LoomBetaAdminOptInOnboardingModalManager = withTransparentErrorBoundary({
	attribution: Attribution.CONTENT_TYPES,
})(LoomBetaAdminOptInOnboardingModalManagerComponent);
